import React from 'react';
import {
  Container,
  Header,
  Table,
  Box,
  Button,
  SpaceBetween,
  StatusIndicator,
  Alert
} from '@amzn/awsui-components-console';

export interface OutputColumn {
  id: string;
  header: string;
  cell?: (item: any) => React.ReactNode;
}

export interface OutputPreviewProps {
  columns: OutputColumn[];
  rows: any[];
  totalEstimate?: number;
  queryTime?: number;
  maxRows?: number;
  state?: 'loading' | 'loaded' | 'empty' | 'truncated' | 'error';
  errorMessage?: string;
  onExport?: () => void;
}

/**
 * OutputPreview - Tabular data preview showing sample query results
 * 
 * Displays query execution results with:
 * - Column headers and data rows
 * - Row count and total estimate
 * - Export to CSV functionality
 * - Query execution time
 * - Loading, empty, truncated, and error states
 * 
 * When to use: After SQL execution or as a preview of expected output format
 */
export const OutputPreview: React.FC<OutputPreviewProps> = ({
  columns,
  rows,
  totalEstimate,
  queryTime,
  maxRows = 10,
  state = 'loaded',
  errorMessage,
  onExport
}) => {
  // Determine if results are truncated
  const isTruncated = state === 'truncated' || (totalEstimate && rows.length < totalEstimate);
  const displayedRows = rows.slice(0, maxRows);

  // Build column definitions for Cloudscape Table
  const columnDefinitions = columns.map(col => ({
    id: col.id,
    header: col.header,
    cell: col.cell || ((item: any) => item[col.id] || '-'),
    sortingField: col.id
  }));

  // Header with row count and export button
  const headerContent = (
    <Header
      variant="h3"
      actions={
        onExport && (state === 'loaded' || state === 'truncated') && rows.length > 0 ? (
          <Button
            iconName="download"
            onClick={onExport}
          >
            Export CSV
          </Button>
        ) : undefined
      }
    >
      {isTruncated && totalEstimate
        ? `Sample Output (${displayedRows.length} of ~${totalEstimate.toLocaleString()} rows)`
        : `Sample Output (${displayedRows.length} row${displayedRows.length !== 1 ? 's' : ''})`
      }
    </Header>
  );

  // Error state
  if (state === 'error') {
    return (
      <Container header={headerContent}>
        <Alert type="error" header="Query execution failed">
          {errorMessage || 'An error occurred while executing the query.'}
        </Alert>
      </Container>
    );
  }

  // Loading state
  if (state === 'loading') {
    return (
      <Container header={<Header variant="h3">Loading results...</Header>}>
        <Box padding={{ vertical: 'l' }}>
          <StatusIndicator type="loading">
            Executing query...
          </StatusIndicator>
        </Box>
      </Container>
    );
  }

  // Empty state
  if (state === 'empty' || rows.length === 0) {
    return (
      <Container header={headerContent}>
        <Box textAlign="center" padding={{ vertical: 'l' }}>
          <Box variant="strong" color="text-body-secondary">
            No results found
          </Box>
          <Box variant="p" color="text-body-secondary" padding={{ top: 's' }}>
            The query returned no rows.
          </Box>
        </Box>
      </Container>
    );
  }

  // Loaded state with data
  return (
    <Container header={headerContent}>
      <SpaceBetween size="m">
        {/* Truncation warning */}
        {isTruncated && (
          <Alert type="info" dismissible={false}>
            Showing {displayedRows.length} of approximately {totalEstimate?.toLocaleString()} rows. 
            Export to CSV to download the full result set.
          </Alert>
        )}

        {/* Data table */}
        <Table
          columnDefinitions={columnDefinitions}
          items={displayedRows}
          variant="embedded"
          sortingDisabled
          empty={
            <Box textAlign="center" color="inherit">
              <b>No data</b>
            </Box>
          }
        />

        {/* Query time footer */}
        {queryTime !== undefined && (
          <Box
            textAlign="right"
            color="text-body-secondary"
            fontSize="body-s"
          >
            Query time: {queryTime.toFixed(1)}s
          </Box>
        )}
      </SpaceBetween>
    </Container>
  );
};

export default OutputPreview;
